import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import SignaturePad from "../../../../../components/UI/SignaturePad";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  createPatientClinicalForm,
  getPatientClinicalForm,
  updatePatientClinicalForm,
} from "../../../../../services/patientService";
import {
  CalendarIcon,
  DeleteIcon,
  NotesIcon,
  PlusIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { clinicalFormCreatePayload } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import { resolveFormActorRole } from "../disciplineFormAccess";
import {
  FORM_FLAG_MESSAGES,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  HeaderControlCell,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  TimeAmPmField,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  DAILY_LOG_FORM_NAME,
  emptyDailyLogPayload,
  emptyVisitEntry,
  normalizeVisitEntries,
  todayYmd,
} from "./dailyLogContent";
import VisitCodeCombobox from "./VisitCodeCombobox";

const LOG_DATE_MAX = new Date().toISOString().slice(0, 10);

const VisitEntrySignature = ({
  entryId,
  imageUrl,
  canEdit,
  printMode,
  onCapture,
  onClear,
}) => {
  const padRef = useRef(null);
  const [resigning, setResigning] = useState(false);
  const showPad = canEdit && !printMode && (!imageUrl || resigning);

  useEffect(() => {
    setResigning(false);
  }, [entryId, imageUrl]);

  if (printMode || (!canEdit && !imageUrl)) {
    return imageUrl ? (
      <img
        src={imageUrl}
        alt="Patient signature"
        className="h-24 w-full object-contain border border-slate-200 rounded bg-white"
      />
    ) : (
      <div className="h-24 flex items-center justify-center text-[11px] text-slate-400 border border-dashed border-slate-200 rounded bg-slate-50">
        No signature
      </div>
    );
  }

  if (!showPad && imageUrl) {
    return (
      <div className="relative border border-slate-200 rounded bg-white overflow-hidden">
        <img
          src={imageUrl}
          alt="Patient signature"
          className="h-28 w-full object-contain"
        />
        {canEdit ? (
          <button
            type="button"
            onClick={() => setResigning(true)}
            className="absolute top-1.5 right-1.5 h-6 px-2 text-[10px] font-semibold rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          >
            Re-sign
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <SignaturePad
        ref={padRef}
        height={112}
        disabled={!canEdit}
        placeholder="Patient sign here"
        onChange={() => {
          const dataUrl = padRef.current?.toDataURL?.();
          if (dataUrl) onCapture?.(dataUrl);
        }}
      />
      {canEdit ? (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              padRef.current?.clear?.();
              onClear?.();
              setResigning(false);
            }}
            className="h-6 px-2 text-[10px] font-semibold rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          >
            Clear
          </button>
          {resigning ? (
            <button
              type="button"
              onClick={() => setResigning(false)}
              className="h-6 px-2 text-[10px] font-semibold rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

VisitEntrySignature.propTypes = {
  entryId: PropTypes.string,
  imageUrl: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onCapture: PropTypes.func,
  onClear: PropTypes.func,
};

const DailyLogForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyDailyLogPayload);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures, handleClinicianSign: handleClinicianSignBase } =
    signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);
  const actorUserId = currentUser?.id ? Number(currentUser.id) : null;

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const updateEntry = (entryId, patch) => {
    setValues((prev) => ({
      ...prev,
      visitEntries: (prev.visitEntries || []).map((row) =>
        row.id === entryId ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addEntry = () => {
    setValues((prev) => ({
      ...prev,
      visitEntries: [...(prev.visitEntries || []), emptyVisitEntry()],
    }));
  };

  const removeEntry = (entryId) => {
    setValues((prev) => {
      const next = (prev.visitEntries || []).filter((row) => row.id !== entryId);
      return {
        ...prev,
        visitEntries: next.length ? next : [emptyVisitEntry()],
      };
    });
  };

  const hydrate = useCallback(
    (data) => {
      const loaded =
        data?.formData &&
        typeof data.formData === "object" &&
        !Array.isArray(data.formData)
          ? data.formData
          : {};
      const base = emptyDailyLogPayload();
      const clinicianSignatureText =
        loaded.clinicianSignatureText || data.clinicianSignatureText || "";
      const clinicianSignedAt =
        loaded.clinicianSignedAt || data.clinicianSignedAt || null;
      const clinicianSignatureName =
        loaded.clinicianSignatureName ||
        data.clinicianSignatureName ||
        data.filledByName ||
        "";

      setForm({
        ...data,
        clinicianSignatureText,
        clinicianSignedAt,
      });
      setValues({
        ...base,
        ...loaded,
        logDate: loaded.logDate || data.scheduleDate || todayYmd(),
        timeIn: loaded.timeIn || "",
        timeOut: loaded.timeOut || "",
        dailyLogsNotes: loaded.dailyLogsNotes || "",
        visitEntries: normalizeVisitEntries(loaded.visitEntries),
        clinicianSignatureName,
        clinicianSignatureText,
        clinicianSignedAt,
        reviewComments:
          data.reviewNotes || data.reviewComments || loaded.reviewComments || "",
      });
      resetSignatures();
    },
    [resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName || draft?.formName || DAILY_LOG_FORM_NAME,
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            formData: emptyDailyLogPayload(),
          });
          finish();
          return;
        }

        const res = await getPatientClinicalForm(patientId, formId);
        if (cancelled) return;
        const data = res.result?.form || res.form;
        hydrate(data);
        finish();
      } catch (err) {
        if (!cancelled) {
          toast.error(err.message || "Failed to load Daily Logs form");
          onBack?.();
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    clinicalForm.formName,
    clinicalForm.formSection,
    clinicalForm.formType,
    currentUser,
    draft?.formName,
    draft?.formSection,
    draft?.formType,
    formId,
    hydrate,
    isDraft,
    onBack,
    onReady,
    patient?.dateOfBirth,
    patientId,
    patientName,
    role,
  ]);

  const { permissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId,
    formFilledByUserId: form?.filledBy || actorUserId,
    formFilledByRole: form?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const canEdit = Boolean(permissions.canEdit) && !printMode;
  const isReviewer = Boolean(permissions.canReview);
  // Assigned clinician e-signs; Admin/OA review without signing (same as other clinical forms).
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const preferredStaffId = draft?.visit?.staffId || null;
  const { caregiverSlot, filledByUserId, assertCaregiverForCreate } =
    useFormCaregiverPicker({
      patientId,
      formType: clinicalForm.formType || draft?.formType || "daily_log_form",
      role,
      canEdit,
      preferredStaffId,
      currentFilledByUserId: form?.filledBy || null,
    });

  const displayForm = useMemo(() => {
    if (!form) return null;
    if (filledByUserId && caregiverSlot) {
      return { ...form, filledBy: filledByUserId };
    }
    return form;
  }, [caregiverSlot, filledByUserId, form]);

  const handleClinicianSign = (signature, signerName) => {
    handleClinicianSignBase(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = () => {
    const payload = {
      logDate: values.logDate || todayYmd(),
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      dailyLogsNotes: values.dailyLogsNotes || "",
      visitEntries: normalizeVisitEntries(values.visitEntries).map((row) => ({
        id: row.id,
        date: row.date || "",
        code: String(row.code || "").trim(),
        timeIn: row.timeIn || "",
        timeOut: row.timeOut || "",
        mileage: row.mileage === "" ? null : row.mileage,
        patientSignatureImage: row.patientSignatureImage || null,
      })),
      clinicianSignatureName: values.clinicianSignatureName || "",
      clinicianSignatureText: values.clinicianSignatureText || null,
      clinicianSignedAt: values.clinicianSignedAt || null,
      // Only include reviewComments in formData; reviewNotes top-level is sent
      // separately and only by reviewers (Admin/OA).
      reviewComments: values.reviewComments || "",
    };

    if (canSign) {
      const sig = signatures.buildSignaturePayload();
      if (sig.clearClinicianSignature) {
        payload.clinicianSignatureText = null;
        payload.clinicianSignedAt = null;
      } else {
        if (sig.clinicianSignatureText) {
          payload.clinicianSignatureText = sig.clinicianSignatureText;
        }
        if (sig.clinicianSignedAt) {
          payload.clinicianSignedAt = sig.clinicianSignedAt;
        }
      }
    }
    return payload;
  };

  const validateForFlag = (flag) => {
    if (!values.logDate) return "Date is required";
    if (flag === "to_be_corrected" && !String(values.reviewComments || "").trim()) {
      return "Review comments are required when marking To Be Corrected";
    }
    const caregiverErr = assertCaregiverForCreate?.();
    if (caregiverErr && (isDraft || flag)) return caregiverErr;
    return null;
  };

  const submit = async (flag) => {
    const err = validateForFlag(flag);
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(flag);
    try {
      const payload = buildPayload();

      if (!formId) {
        if (!draft?.formType) {
          throw new Error("Missing Daily Logs draft");
        }
        const createRes = await createPatientClinicalForm(
          patientId,
          clinicalFormCreatePayload(draft, {
            formData: payload,
            flag,
            filledByUserId,
            scheduleDate: payload.logDate,
          }),
        );
        const created = createRes.result?.form || createRes.form;
        if (!created?.id) throw new Error("Failed to create Daily Logs form");
        // Persist review notes / schedule when create payload did not map them.
        const updateRes = await updatePatientClinicalForm(
          patientId,
          created.id,
          {
            formData: payload,
            flag,
            ...(filledByUserId != null ? { filledByUserId } : {}),
            scheduleDate: payload.logDate,
            // Only Admin/OA may write review notes.
            ...(isReviewer ? { reviewNotes: payload.reviewComments || null } : {}),
          },
        );
        const saved = updateRes.result?.form || updateRes.form || created;
        hydrate(saved);
        toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
        onCreated?.(created.id);
        return;
      }

      const updateRes = await updatePatientClinicalForm(patientId, formId, {
        formData: payload,
        flag,
        ...(filledByUserId != null ? { filledByUserId } : {}),
        scheduleDate: payload.logDate,
        // Only Admin/OA may write review notes.
        ...(isReviewer ? { reviewNotes: payload.reviewComments || null } : {}),
      });
      const saved = updateRes.result?.form || updateRes.form;
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
    } catch (error) {
      toast.error(error.message || "Failed to save Daily Logs form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const entries = values.visitEntries || [];
  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Daily Log
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Daily visit documentation for home health services.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={displayForm}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(patient?.dateOfBirth || form.patientDateOfBirth)}
          caregiverSlot={caregiverSlot}
          dateLabel="Date"
          dateValue={displayDateUS(values.logDate)}
          dateSlot={
            printMode ? null : (
              <HeaderControlCell label="Date">
                <InputDatePicker
                  name="logDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.logDate) || ""}
                  onChange={(event) => setValue("logDate", event.target.value)}
                  max={LOG_DATE_MAX}
                  className="min-w-0 w-full"
                  inputClassName="w-full h-4 text-[10px] leading-4 font-bold text-slate-900 bg-transparent border-0 p-0 outline-none"
                />
              </HeaderControlCell>
            )
          }
          visitMeta={{
            timeIn: values.timeIn,
            timeOut: values.timeOut,
            canEdit,
            printMode,
            showBillingCode: false,
            onTimeInChange: (next) => setValue("timeIn", next),
            onTimeOutChange: (next) => {
              setValue("timeOut", next);
            },
          }}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}
        {!printMode && (
          <OfficeCommentsNote comments={form.reviewNotes || values.reviewComments} />
        )}

            <Section icon={NotesIcon} title="1. Daily Notes">
          <Field label="Notes">
            {printMode || !canEdit ? (
              <div className="min-h-[72px] whitespace-pre-wrap text-[12px] text-slate-800">
                {values.dailyLogsNotes || "—"}
              </div>
            ) : (
              <textarea
                className={`${textareaClass} min-h-[88px]`}
                value={values.dailyLogsNotes || ""}
                placeholder="Enter daily log notes…"
                onChange={(e) => setValue("dailyLogsNotes", e.target.value)}
              />
            )}
          </Field>
        </Section>

        <Section icon={CalendarIcon} title="2. Visit Entries">
          <div className="space-y-3">
            {entries.map((entry, index) => (
              <div
                key={entry.id}
                className="rounded-md border border-slate-200 overflow-hidden"
              >
                <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-slate-50 border-b border-slate-200">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                    Entry {index + 1}
                  </p>
                  {canEdit && entries.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => removeEntry(entry.id)}
                      className="inline-flex items-center justify-center h-6 w-6 rounded text-rose-600 hover:bg-rose-50"
                      title="Remove entry"
                      aria-label={`Remove entry ${index + 1}`}
                    >
                      <DeleteIcon size={13} />
                    </button>
                  ) : null}
                </div>

                <div className="p-3 space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-3 gap-y-2 items-end">
                    <Field label="Date">
                      {printMode || !canEdit ? (
                        <div className="h-7 flex items-center text-[12px] font-semibold text-slate-900">
                          {displayDateUS(entry.date) || "—"}
                        </div>
                      ) : (
                        <InputDatePicker
                          name={`entry-date-${entry.id}`}
                          formatSeparator="/"
                          allowManualEntry
                          compact
                          value={formatDateForInput(entry.date) || ""}
                          onChange={(event) =>
                            updateEntry(entry.id, { date: event.target.value })
                          }
                          max={LOG_DATE_MAX}
                          className="min-w-0 w-full"
                          inputClassName={`${fieldClass} pr-7`}
                        />
                      )}
                    </Field>
                    <Field label="Code">
                      {printMode || !canEdit ? (
                        <div className="h-7 flex items-center text-[12px] font-semibold text-slate-900">
                          {entry.code || "—"}
                        </div>
                      ) : (
                        <VisitCodeCombobox
                          value={entry.code || ""}
                          onChange={(next) =>
                            updateEntry(entry.id, { code: next })
                          }
                        />
                      )}
                    </Field>
                    <Field label="Time In">
                      <TimeAmPmField
                        label="Time In"
                        name={`entry-time-in-${entry.id}`}
                        value={entry.timeIn}
                        canEdit={canEdit}
                        printMode={printMode}
                        showLabel={false}
                        controlClassName={fieldClass}
                        onChange={(next) =>
                          updateEntry(entry.id, {
                            timeIn: next,
                          })
                        }
                      />
                    </Field>
                    <Field label="Time Out">
                      <TimeAmPmField
                        label="Time Out"
                        name={`entry-time-out-${entry.id}`}
                        value={entry.timeOut}
                        canEdit={canEdit}
                        printMode={printMode}
                        showLabel={false}
                        controlClassName={fieldClass}
                        minTime={entry.timeIn}
                        onChange={(next) =>
                          updateEntry(entry.id, { timeOut: next })
                        }
                      />
                    </Field>
                    <Field label="Mileage">
                      {printMode || !canEdit ? (
                        <div className="h-7 flex items-center text-[12px] font-semibold text-slate-900">
                          {entry.mileage || "—"}
                        </div>
                      ) : (
                        <input
                          type="text"
                          inputMode="decimal"
                          className={fieldClass}
                          value={entry.mileage}
                          placeholder="0"
                          onChange={(e) =>
                            updateEntry(entry.id, {
                              mileage: e.target.value.replace(/[^\d.]/g, ""),
                            })
                          }
                        />
                      )}
                    </Field>
                  </div>

                  <Field label="Patient Signature">
                    <VisitEntrySignature
                      entryId={entry.id}
                      imageUrl={entry.patientSignatureImage}
                      canEdit={canEdit}
                      printMode={printMode}
                      onCapture={(dataUrl) =>
                        updateEntry(entry.id, {
                          patientSignatureImage: dataUrl,
                        })
                      }
                      onClear={() =>
                        updateEntry(entry.id, { patientSignatureImage: null })
                      }
                    />
                  </Field>
                </div>
              </div>
            ))}

            {canEdit ? (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={addEntry}
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  <PlusIcon size={12} />
                  Add Entry
                </button>
              </div>
            ) : null}
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection={false}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {!printMode && isReviewer ? (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(next) => setValue("reviewComments", next)}
          />
        ) : null}

        {!printMode ? (
          <FormActionBar
            permissions={permissions}
            status={form.status}
            canEdit={canEdit}
            saving={saving}
            onSubmit={submit}
            printMode={printMode}
          />
        ) : null}
      </div>
    </div>
  );
};

DailyLogForm.propTypes = {
  patient: PropTypes.object.isRequired,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default DailyLogForm;
